import { useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';

/**
 * A VISSZA gomb / gesztus elobb a felso reteget zarja be (versenyaudit K2).
 *
 * Telefonon a vissza-mozdulat a legtermeszetesebb "bezaras". Eddig viszont a
 * nyitott parbeszedet, menulapot, keresot atugorta, es kivitt a csatornabol -
 * az appokban ez mindig a legfelso reteget zarja.
 *
 * ─── Hogyan ─────────────────────────────────────────────────────────────────
 *
 * Amig BARMILYEN reteg nyitva van, a mostani hely fole EGY jelzo-bejegyzes
 * kerul az elozmenybe (ugyanaz a cim). A vissza-lepes ezt veszi le: a
 * legfelso reteg bezarul, es ha maradt meg nyitott, a jelzo visszakerul.
 *
 * Hogy miert EGY kozos jelzo, es nem retegenkent egy: a parbeszedek gyakran
 * kicserelodnek (betoltes -> tartalom), es fejlesztoi modban a React minden
 * hatast ketszer futtat. Retegenkenti "fel/le" bejegyzesekkel a fuggo
 * vissza-lepesek es az uj bejegyzesek osszeakadtak, es a vegen az egesz
 * alkalmazasbol kiszaltunk. A jelzot ezert csak akkor vesszuk le, ha egy
 * pillanattal kesobb mar TENYLEG nincs nyitott reteg.
 *
 * Csak ERINTOKEPERNYON: asztali gepen a bongeszo vissza-gombja maradjon az,
 * ami volt - ott a parbeszedet Esc-pel zarjak.
 */
const OVERLAY_KEY = '__avslackOverlay';

type RouterHistory = ReturnType<typeof useRouter>['history'];

const coarse = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

let nextId = 1;
const stack: { id: number; close: () => void }[] = [];
let subscribed: RouterHistory | null = null;

const stateOf = (history: RouterHistory) =>
  history.location.state as unknown as Record<string, unknown>;
const hasMarker = (history: RouterHistory) => stateOf(history)[OVERLAY_KEY] === true;

function pushMarker(history: RouterHistory): void {
  if (hasMarker(history)) return;
  history.push(history.location.href, { ...stateOf(history), [OVERLAY_KEY]: true } as never);
}

function ensureSubscribed(history: RouterHistory): void {
  if (subscribed === history) return;
  subscribed = history;
  history.subscribe(() => {
    // A jelzo lekerult (vissza-lepes, vagy mashova navigaltunk): a felso reteg zarul.
    if (hasMarker(history) || stack.length === 0) return;
    const top = stack.pop();
    top?.close();
    if (stack.length > 0) pushMarker(history);
  });
}

export function useBackToClose(open: boolean, close: () => void): void {
  const router = useRouter();
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open || !coarse()) return;
    const history = router.history;
    ensureSubscribed(history);

    const id = nextId++;
    stack.push({ id, close: () => closeRef.current() });
    pushMarker(history);

    return () => {
      const index = stack.findIndex((entry) => entry.id === id);
      if (index >= 0) stack.splice(index, 1);
      // Gombbal / Esc-pel zarult: ha egy pillanat mulva sincs nyitott reteg,
      // a jelzot levesszuk (kicserelt parbeszednel addigra mar van uj).
      window.setTimeout(() => {
        if (stack.length === 0 && hasMarker(history)) history.back();
      }, 60);
    };
  }, [open, router]);
}
